export default function KeyLargoDestinationsSection() {
  const stats = [
    {
      value: "58%",
      label: "of Key Largo requests are dive or marine-related travel with under 3 weeks notice",
    },
    {
      value: "2–3 weeks",
      label: "average notice for liveaboard bookings and dive group travel",
    },
    {
      value: "Nov–Apr",
      label: "peak dive travel season — passport renewals spike before Cozumel and Bahamas trips",
    },
    {
      value: "48-hour",
      label: "shortest liveaboard departure window we've accommodated for a Key Largo instructor",
    },
  ];

  const destinations = [
    {
      icon: "ri-map-pin-2-line",
      name: "The Bahamas",
      context: "The closest international destination to Key Largo — Bimini is just 50 miles across the Gulf Stream. Liveaboard boats depart Miami and Fort Lauderdale for week-long expeditions to the Exuma Cays, Long Island, and Cat Island. Key Largo divers regularly participate in hammerhead shark dives, blue hole descents, and wall dives beyond recreational limits. Day trips and weekend excursions to Bimini and West End are accessible by ferry and boat.",
      urgency: "Liveaboard berths and dive expedition invitations often arrive with 1–2 weeks of notice — insufficient time for standard passport processing",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Mexico — Cozumel & Riviera Maya",
      context: "Cozumel is the single most popular international dive destination for Key Largo divers. The island's drift diving along Palancar Reef, Colombia Deep, and Santa Rosa Wall offers reliable warm-water diving when local Keys conditions are windier. Cenote diving in the Riviera Maya — Dos Ojos, The Pit, Angelita — draws technical divers and underwater photographers from Key Largo's marine community. Mexico requires six months of passport validity for entry.",
      urgency: "Winter dive trips to Cozumel surge from November through March — passport discoveries spike in December and January for Key Largo divers",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Belize",
      context: "Belize's barrier reef — the second-largest in the world — draws Key Largo divers and marine biologists for world-class wall diving, the Great Blue Hole, and marine conservation projects. Half Moon Caye, Lighthouse Reef, and Turneffe Atoll offer diving experiences that complement Key Largo's own reef expertise. Marine researchers from Key Largo regularly collaborate with Belizean conservation organizations on coral restoration and fish population studies.",
      urgency: "Research expedition invitations and dive trip bookings to Belize often arrive with 2–4 weeks of notice",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Caribbean Cruises",
      context: "Key Largo residents regularly cruise from Miami and Fort Lauderdale to the Eastern and Western Caribbean — the Bahamas, Jamaica, the Cayman Islands, Cozumel, the Virgin Islands, and St. Maarten. While Key Largo itself offers world-class marine experiences, cruises provide a different kind of vacation: multiple islands, all-inclusive dining, and family-friendly activities. All cruise passengers need valid passports.",
      urgency: "Cruise embarkation deadlines are immovable — passport issues discovered days before sailing require emergency processing",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Cayman Islands",
      context: "Grand Cayman, Cayman Brac, and Little Cayman offer some of the Caribbean's most pristine diving — Bloody Bay Wall, Stingray City, and Kittiwake wreck. Key Largo divers frequently travel to the Caymans for underwater photography workshops, technical diving courses, and family dive vacations. The islands' marine protected areas and conservation efforts align with the values of Key Largo's diving community.",
      urgency: "Photography workshop and certification course bookings often require 2–3 weeks of advance notice",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Central America — Utila, Roatan, Bonaire",
      context: "Utila, Honduras is one of the world's most affordable and reliable whale shark encounter destinations, drawing Key Largo divers during the spring and summer months. Roatan offers affordable dive training and coral reef experiences. Bonaire's shore diving and marine park protection make it a favorite for underwater photographers and macro enthusiasts from Key Largo's dive shops. These destinations represent the cutting edge of accessible marine adventure travel from the Keys.",
      urgency: "Budget liveaboard and hostel dive trip bookings often happen with 1–3 weeks of notice for spontaneous travelers",
    },
  ];

  return (
    <section id="destinations" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Where Key Largo Travelers Go</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Popular International Destinations from Key Largo
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Key Largo travelers head to the Bahamas for liveaboard diving, Cozumel for cenote and reef dives, Belize for the Great Blue Hole, and the Caribbean for cruises. These are the destinations that create the most passport urgency for our Key Largo clients.
          </p>
        </div>

        {/* Stat blocks */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-14">
          {stats.map((stat, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-5 border border-gray-200 text-center">
              <p className="font-serif text-2xl md:text-3xl font-bold text-[#1A365D] mb-1">{stat.value}</p>
              <p className="text-gray-500 text-xs leading-relaxed">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Destination cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
          {destinations.map((dest, i) => (
            <div
              key={i}
              className="bg-[#F7F8FA] rounded-lg border border-gray-200 p-6 hover:border-[#F6C344] transition-all duration-200 flex flex-col"
            >
              <div className="flex items-center gap-2.5 mb-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-white border border-gray-200 flex-shrink-0">
                  <i className={`${dest.icon} text-[#1A365D] text-sm`}></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-sm">{dest.name}</h3>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed mb-4 flex-1">{dest.context}</p>
              <div className="bg-white rounded-md p-3 border border-gray-200">
                <p className="text-[#F6C344] text-xs font-semibold mb-0.5"><i className="ri-time-line mr-1"></i>Urgency Pattern</p>
                <p className="text-gray-500 text-xs leading-relaxed">{dest.urgency}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Internal links */}
        <div className="text-center">
          <p className="text-gray-400 text-xs mb-3">Serving travelers throughout South Florida</p>
          <div className="flex flex-wrap items-center justify-center gap-2">
            {[
              { name: "Key West", href: "/key-west" },
              { name: "Naples", href: "/naples" },
              { name: "Bonita Springs", href: "/bonita-springs" },
              { name: "Marco Island", href: "/marco-island" },
              { name: "Fort Myers", href: "/fort-myers" },
              { name: "Miami", href: "/aventura" },
            ].map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="inline-flex items-center gap-1 text-xs text-[#1A365D] hover:text-[#F6C344] font-medium transition-colors bg-[#F7F8FA] border border-gray-200 hover:border-[#F6C344] rounded-full px-3 py-1.5"
              >
                {link.name} <i className="ri-arrow-right-line text-[10px]"></i>
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}